import { httpClient } from './httpClient';
import { Comment } from '../types/comment';

export interface CommentRequest {
  content: string;
}

export interface AddCommentResponse {
  id: number;
}

/**
 * Comment service for handling ticket comments
 * 
 * IMPORTANT: This service uses the following API endpoints:
 * - GET /comments/ticket/:ticketId - Get all comments for a ticket
 * - POST /tickets/:ticketId/comments - Add a comment to a ticket (uses ticketController.addComment)
 * - PUT /comments/:commentId - Update a comment
 * - DELETE /comments/:commentId - Delete a comment
 */
export const commentService = {
  /**
   * Get all comments for a ticket
   * @param ticketId - The ID of the ticket
   */
  async getTicketComments(ticketId: string | number): Promise<Comment[]> {
    return httpClient.get<Comment[]>(`/api/comments/ticket/${ticketId}`);
  },

  /**
   * Add a comment to a ticket
   * @param ticketId - The ID of the ticket
   * @param data - The comment data (content)
   */
  async addComment(ticketId: string | number, data: CommentRequest): Promise<AddCommentResponse> {
    // Use the ticket-based endpoint which triggers notifications
    return httpClient.post<AddCommentResponse>(`/api/tickets/${ticketId}/comments`, data);
  },

  /**
   * Update a comment
   * @param commentId - The ID of the comment
   * @param data - The updated comment data
   */
  async updateComment(commentId: number, data: CommentRequest): Promise<void> {
    return httpClient.put<void>(`/api/comments/${commentId}`, data);
  },

  /**
   * Delete a comment
   * @param commentId - The ID of the comment
   */
  async deleteComment(commentId: number): Promise<void> {
    return httpClient.delete<void>(`/api/comments/${commentId}`);
  }
};
